'use client';

import React, { useState, useEffect } from 'react';
import { ShoppingCart, Smartphone } from 'lucide-react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ecoBuilder/ui/button';
import { usePageContext } from '@/components/ecoBuilder/renderer/PageContext';
import { useSite } from '@/components/ecoBuilder/store/SiteContext';
import { EditorField } from '@/config/ecoBuilder/types';
import { RichTextRenderer } from '@/components/ecoBuilder/atoms/RichTextRenderer';

export interface StickyMobileBarProps {
    showThumbnail?: boolean;
    buttonText?: string;
    position?: 'bottom' | 'top';
    productImage?: string;
    productTitle?: string;
    visibility?: 'all' | 'mobile' | 'desktop';
    className?: string; // For Editor
    // NEW: Style overrides
    backgroundColor?: string;
    textColor?: string;
    buttonColor?: string;
}

export const StickyMobileBar: React.FC<StickyMobileBarProps> = ({
    showThumbnail = true,
    buttonText = 'أطلب الآن - الدفع عند الاستلام',
    position = 'bottom',
    productImage,
    productTitle = 'عرض خاص',
    visibility = 'mobile',
    className,
    backgroundColor,
    textColor,
    buttonColor
}) => {
    const { themeSettings } = usePageContext();
    const { isEditor } = useSite();

    // Theme Defaults
    const primaryColor = themeSettings?.primaryColor || '#7c3aed';

    const finalButtonColor = buttonColor || primaryColor;
    const finalTextColor = textColor || themeSettings?.textColor || '#09090b';

    const [isVisible, setIsVisible] = useState(false);
    const [mounted, setMounted] = useState(false);

    // Initial mount check
    useEffect(() => {
        setMounted(true);
    }, []);

    // Scroll Listener
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 300) {
                setIsVisible(true);
            } else {
                setIsVisible(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToCheckout = () => {
        // Smart search
        const checkoutPossibleTargets = [
            '#express-order-form',
            '.express-order-form',
            '#checkout-section',
            '#checkout',
            'form[action*="order"]',
            'form'
        ];

        for (const selector of checkoutPossibleTargets) {
            const el = document.querySelector(selector);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const input = el.querySelector('input');
                if (input) input.focus();
                break;
            }
        }
    };

    if (!mounted) return null;

    let visibilityClasses = "";
    if (visibility === 'mobile') visibilityClasses = "flex md:hidden";
    else if (visibility === 'desktop') visibilityClasses = "hidden md:flex";
    else visibilityClasses = "flex";

    // Editor Preview
    if (isEditor) {
        return (
            <div className={cn(
                "w-full py-4 text-center rounded-lg border-2 border-dashed border-primary/20 transition-all hover:border-primary/60",
                "bg-background/50",
                className
            )}>
                <div className="flex flex-col items-center justify-center gap-2">
                    <div className="flex items-center gap-2 text-primary font-semibold">
                        <Smartphone className="w-5 h-5" />
                        <span>شريط التثبيت الذكي ({visibility === 'desktop' ? 'كمبيوتر فقط' : visibility === 'mobile' ? 'جوال فقط' : 'الكل'})</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        سيظهر هذا الشريط {position === 'top' ? 'أعلى' : 'أسفل'} الشاشة عند التمرير.
                    </p>

                    {/* Preview - Swapped Order: Info First (Right), Button Second (Left) */}
                    <div className="mt-4 w-full max-w-sm mx-auto p-2 border rounded bg-background shadow-sm flex items-center justify-between gap-2" dir="rtl">
                        <div className="flex items-center gap-2 flex-1 justify-start">
                            {showThumbnail && (
                                <div className="w-8 h-8 rounded bg-gray-200 shrink-0"
                                    style={productImage ? { backgroundImage: `url(${productImage})`, backgroundSize: 'cover' } : undefined}
                                />
                            )}
                            <div className="text-right flex-1 min-w-0">
                                <RichTextRenderer html={productTitle} className="text-xs font-bold truncate leading-tight" />
                            </div>
                        </div>
                        <Button size="sm" style={{ backgroundColor: finalButtonColor, color: '#fff' }} className="h-8 text-xs shrink-0 flex items-center gap-1">
                            <RichTextRenderer html={buttonText} inline={true} className="pointer-events-none" />
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    const content = (
        <div
            className={cn(
                "fixed left-0 right-0 z-[100] px-4 py-3 transition-all duration-300 ease-in-out",
                "bg-white/80 dark:bg-zinc-900/80 backdrop-blur-lg border-t border-white/20 shadow-[0_-5px_15px_-5px_rgba(0,0,0,0.1)]",
                position === 'bottom' ? "bottom-0 safe-area-bottom" : "top-0 border-b border-t-0 safe-area-top",
                isVisible ? "translate-y-0 opacity-100" : (position === 'bottom' ? "translate-y-[120%] opacity-0" : "-translate-y-[120%] opacity-0"),
                "items-center gap-4 justify-between",
                visibilityClasses
            )}
            style={{
                backgroundColor: backgroundColor ? backgroundColor : undefined,
                color: finalTextColor
            }}
            dir="rtl"
        >
            {/* 1. Product Info (Now FIRST -> Right Side in RTL) */}
            <div className="flex items-center gap-3 flex-1 min-w-0 justify-start text-right">
                {showThumbnail && productImage && (
                    <img
                        src={productImage}
                        alt="Product"
                        className="w-10 h-10 rounded-lg object-cover border border-black/5 shadow-sm bg-white shrink-0"
                    />
                )}
                <div className="flex flex-col flex-1 min-w-0">
                    <RichTextRenderer
                        html={productTitle}
                        className="text-sm font-semibold truncate leading-tight [&_p]:m-0 [&_span]:m-0"
                    />
                </div>
            </div>

            {/* 2. CTA Button (Now SECOND -> Left Side in RTL) */}
            <div className="flex-shrink-0">
                <Button
                    onClick={scrollToCheckout}
                    size="default"
                    className="shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all font-bold px-6 rounded-full flex items-center gap-2"
                    style={{ backgroundColor: finalButtonColor, color: '#ffffff' }}
                >
                    <RichTextRenderer
                        html={buttonText}
                        inline={true}
                        className="pointer-events-none"
                    />
                    <ShoppingCart className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );

    return createPortal(content, document.body);
};


// ═══════════════════════════════════════════════════════════════
// EDITOR CONFIGURATION
// ═══════════════════════════════════════════════════════════════

export const StickyMobileBarDefaultProps: Partial<StickyMobileBarProps> = {
    showThumbnail: true,
    buttonText: '<p>أطلب الآن</p>', // Updated default to simulate what Tiptap might output, or keep plain. RichTextRenderer handles plain too.
    position: 'bottom',
    productTitle: '<p>عرض خاص</p>',
    visibility: 'mobile'
};

export const StickyMobileBarSchema: EditorField[] = [
    {
        key: 'visibility',
        label: 'الأجهزة المستهدفة',
        type: 'select',
        options: [
            { value: 'mobile', label: '📱 الهاتف فقط (Mobile Only)' },
            { value: 'desktop', label: '💻 الكمبيوتر فقط (Desktop Only)' },
            { value: 'all', label: '🌍 كل الأجهزة (All Devices)' }
        ],
        defaultValue: 'mobile',
        description: 'اختر متى يجب أن يظهر هذا الشريط.'
    },
    {
        key: 'buttonText',
        label: 'نص الزر',
        type: 'richtext', // Enables Tiptap Toolbar
        defaultValue: 'أطلب الآن'
    },
    {
        key: 'productTitle',
        label: 'العنوان المصغر',
        type: 'richtext', // Enables Tiptap Toolbar
        placeholder: 'عرض خاص...'
    },
    {
        key: 'showThumbnail',
        label: 'عرض الصورة المصغرة',
        type: 'boolean',
        defaultValue: true
    },
    {
        key: 'productImage',
        label: 'صورة المنتج',
        type: 'image',
        description: 'اختر صورة من المعرض لتظهر في الشريط.'
    },
    {
        key: 'position',
        label: 'المكان',
        type: 'select',
        options: [
            { value: 'bottom', label: 'أسفل الشاشة' },
            { value: 'top', label: 'أعلى الشاشة' }
        ]
    },
    // Colors
    {
        key: 'buttonColor',
        label: 'لون الزر',
        type: 'color',
        description: 'اتركه فارغاً لاستخدام اللون الأساسي للإعدادات.'
    },
    {
        key: 'backgroundColor',
        label: 'لون الخلفية',
        type: 'color',
        description: 'اتركه فارغاً لاستخدام خلفية شفافة ضبابية.'
    },
    {
        key: 'textColor',
        label: 'لون النص',
        type: 'color',
        description: 'اتركه فارغاً لاستخدام اللون الافتراضي.'
    }
];
